import { FormlyFieldConfig } from '@ngx-formly/core';

interface VisibleWhenConfig {
  fieldId: string;
  values: string[];
}

function normalizeVisibleWhen(raw: unknown): VisibleWhenConfig | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const visibleWhen = raw as { fieldId?: unknown; values?: unknown };
  if (typeof visibleWhen.fieldId !== 'string' || visibleWhen.fieldId.trim() === '') {
    return null;
  }

  const valuesRaw = visibleWhen.values;
  const values = Array.isArray(valuesRaw)
    ? valuesRaw.map((value) => String(value ?? ''))
    : typeof valuesRaw === 'string'
      ? valuesRaw
          .split(',')
          .map((value) => value.trim())
          .filter((value) => value !== '')
      : [];

  if (values.length === 0) {
    return null;
  }

  return {
    fieldId: visibleWhen.fieldId,
    values,
  };
}

export function applyVisibilityConditions(fields: FormlyFieldConfig[]): FormlyFieldConfig[] {
  return fields.map((field) => {
    const nextField: FormlyFieldConfig = {
      ...field,
      props: field.props ? { ...field.props } : undefined,
    };

    const visibleWhen = normalizeVisibleWhen(nextField.props?.['visibleWhen']);
    if (visibleWhen) {
      const existingHideExpression = nextField.hideExpression;
      const visibilityExpression = (model: Record<string, unknown>) => {
        const raw = model?.[visibleWhen.fieldId];
        // Multi-value controlling fields (checkbox-group / multi-select) are
        // visible when ANY selected value is in the configured set.
        const selected = Array.isArray(raw)
          ? raw.map((value) => (value === null || value === undefined ? '' : String(value)))
          : [raw === null || raw === undefined ? '' : String(raw)];
        const matches = selected.some((value) => visibleWhen.values.includes(value));
        return !matches;
      };

      if (!existingHideExpression) {
        nextField.hideExpression = visibilityExpression;
      } else if (typeof existingHideExpression === 'function') {
        nextField.hideExpression = (model: Record<string, unknown>, formState?: unknown, f?: any) =>
          Boolean(existingHideExpression(model, formState, f)) || visibilityExpression(model);
      } else {
        // Formly also supports string `hideExpression`s, but this codebase never
        // persists one on a submission field — visibility is the only source of
        // `hideExpression` here. Rather than silently dropping the configured
        // visibility condition (the previous behaviour), apply it directly.
        nextField.hideExpression = visibilityExpression;
      }
    }

    if (Array.isArray(field.fieldGroup) && field.fieldGroup.length > 0) {
      nextField.fieldGroup = applyVisibilityConditions(field.fieldGroup);
    }

    if (field.fieldArray && typeof field.fieldArray === 'object') {
      nextField.fieldArray = {
        ...field.fieldArray,
        fieldGroup: Array.isArray(field.fieldArray.fieldGroup)
          ? applyVisibilityConditions(field.fieldArray.fieldGroup)
          : field.fieldArray.fieldGroup,
      };
    }

    return nextField;
  });
}
